import React, { useState, useCallback, useMemo } from 'react';
import useLocale from '@/hooks/useLocale';
import * as Styles from './AvailableOptions.styles';
import { useSelector } from 'react-redux';
import { IconCheck } from 'gatorcomponents';
import ContentSection from '@/pages/Products/VpsOciDetailPage/components/ContentSection';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils/getIconBySlug';

import ModalOperationalSystems from './components/ModalOperationalSystems/ModalOperationalSystems';
import ModalPanels from './components/ModalPanels/ModalPanels';
import ModalApplications from './components/ModalApplications/ModalApplications';

const AvailableOptions = () => {
  const [showModal, setShowModal] = useState(null);

  const vpsOci = useSelector(state => state.vpsOci);
  const {
    availableOptions, vps,
  } = (vpsOci && vpsOci.data) || {};

  const { operationalSystems, panels, applications } = availableOptions || {};
  const hasOperationalSystems = operationalSystems && operationalSystems.length > 0;
  const hasPanels = panels && panels.length > 0;
  const hasApplications = applications && applications.length > 0;

  const { productDetails: productDetailsLocale } = useLocale();
  const { store: storeLocale } = productDetailsLocale.vpsOci;

  const [activeTab, setActiveTab] = useState('operationalSystems');
  const [selectedItem, setSelectedItem] = useState(null);

  const handleTabClick = useCallback((tabKey) => {
    setActiveTab(tabKey);
    setSelectedItem(null);
  }, []);

  const handleItemClick = useCallback((slug) => {
    setSelectedItem(slug);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const renderModalContent = useMemo(() => {
    if (!showModal) return null;

    switch (activeTab) {
      case 'operationalSystems':
        return (
          <ModalOperationalSystems
            onClose={handleCloseModal}
            selectedItem={selectedItem}
          />
        );
      case 'panels':
        return (
          <ModalPanels
            onClose={handleCloseModal}
            selectedItem={selectedItem}
          />
        );
      case 'applications':
        return (
          <ModalApplications
            onClose={handleCloseModal}
            selectedItem={selectedItem}
          />
        );
      default:
        return null;
    }
  }, [activeTab, showModal, handleCloseModal, selectedItem]);

  const TabsHeader = useMemo(() => {
    let tabsConfig = [
      { key: 'operationalSystems', label: storeLocale.changeApplications.tabs.operationalSystems },
      { key: 'panels', label: storeLocale.changeApplications.tabs.panels },
      { key: 'applications', label: storeLocale.changeApplications.tabs.applications },
    ];

    if (!hasOperationalSystems) {
      tabsConfig = tabsConfig.filter(tab => tab.key !== 'operationalSystems');
    }

    if (!hasPanels) {
      tabsConfig = tabsConfig.filter(tab => tab.key !== 'panels');
    }

    if (!hasApplications) {
      tabsConfig = tabsConfig.filter(tab => tab.key !== 'applications');
    }

    return (
      <Styles.TabsHeader data-testid="vpsoci-templates-tabs-header">
        {tabsConfig.map(tab => (
          <Styles.TabsHeaderItem
            key={tab.key}
            $active={activeTab === tab.key}
            onClick={() => handleTabClick(tab.key)}
            data-testid={`vpsoci-templates-tab-header-${tab.key}`}
          >
            {activeTab === tab.key && (
              <IconCheck
                size="sm"
                color="primaryMedium"
                testId={`active-tab-${tab.key}`}
              />
            )}
            {tab.label}
          </Styles.TabsHeaderItem>
        ))}
      </Styles.TabsHeader>
    );
  }, [activeTab, handleTabClick, storeLocale.changeApplications.tabs, hasOperationalSystems, hasPanels, hasApplications]);

  const TabsContent = useMemo(() => {
    const options = availableOptions && availableOptions[activeTab];
    if (!options || options.length === 0) return null;

    return (
      <Styles.TabsContent data-testid={`vpsoci-templates-tab-content-${activeTab}`}>
        {options.map((item) => {
          if (!item.slug) return null;

          const itemName = item.name ? item.name : null;
          const iconItem = getIconBySlug(item.slug);
          const isSelected = selectedItem === item.slug;
          return (
            <Styles.TabContentItem
              key={item.slug}
              onClick={() => handleItemClick(item.slug)}
              $selected={isSelected}
              data-testid={`vpsoci-templates-tab-item-${item.slug}`}
            >
              {iconItem && <Styles.TabContentItemIcon src={iconItem} alt={item.slug} data-testid={`vpsoci-templates-tab-item-icon-${item.slug}`} />}
              {itemName && <Styles.TabContentItemTitle>{itemName}</Styles.TabContentItemTitle>}
            </Styles.TabContentItem>
          );
        })}
      </Styles.TabsContent>
    );
  }, [availableOptions, activeTab, selectedItem, handleItemClick]);

  if (!vps || Object.keys(vps).length === 0) return null;
  if (!availableOptions || Object.keys(availableOptions).length === 0) return null;

  const hasAtLeastOneOption = Object.values(availableOptions).some(
    optionList => Array.isArray(optionList) && optionList.length > 0,
  );

  if (!hasAtLeastOneOption) return null;

  return (
    <ContentSection testId="vpsoci-templates-changeApplications-container">
      <Styles.Subtitle data-testid="vpsoci-templates-changeApplications-title">{storeLocale.changeApplications.title}</Styles.Subtitle>
      <Styles.Description data-testid="vpsoci-templates-changeApplications-description">{storeLocale.changeApplications.description}</Styles.Description>

      <Styles.TabsWrapper data-testid="vpsoci-templates-tabs-wrapper">
        {TabsHeader}
        {TabsContent}
      </Styles.TabsWrapper>

      {showModal && renderModalContent}
    </ContentSection>
  );
};

export default AvailableOptions;
